/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useEffect } from "react";
import { RouterChildContext, useHistory } from "react-router";
import useNavigate, { NavigateFunc } from "../../../../Basis/Utils/useNavigate";
import ViewModel from "../../../../Basis/ViewModels/ViewModel";
import { cloudTypen } from "../../../DomainDrivenDesign/Queries/CloudTypen";
import MarkiereBenachrichtigungenAlsGelesen, { BenachrichtigungVersion, MarkiereBenachrichtigungenAlsGelesenInput } from "../../Commands/MarkiereBenachrichtigungenAlsGelesen";
import Benachrichtigung from "../../Queries/Benachrichtigung";
import LadeBenachrichtigungen from "../../Queries/LadeBenachrichtigungen";
import { Symbole } from "../../Queries/Symbol";
import { useAppNotificationContext } from "../AppNotificationContext";
import IAppNotificationContext from "../AppNotificationContext/IAppNotificationContext";
import AppNotificationsState, { createInitialState } from "./AppNotificationsState";
import IAppNotificationItem from "./IAppNotificationItem";

export default class AppNotificationsModel extends ViewModel<AppNotificationsState> {
    private static readonly _klingelnMillisekunden = 2000;
    private static readonly _toastSekunden = 5;
    private static readonly _toastIntervallMillisekunden = 1000;
    private readonly _navigate: NavigateFunc;
    private readonly _ladeBenachrichtigungen: LadeBenachrichtigungen;
    private readonly _markiereBenachrichtigungenAlsGelesen: MarkiereBenachrichtigungenAlsGelesen;
    private readonly _appNotificationContext: IAppNotificationContext;
    private readonly _history: RouterChildContext['router']['history'];

    public constructor() {
        super({
            initialState: createInitialState(),
        });
        this._navigate = useNavigate();
        this._ladeBenachrichtigungen = new LadeBenachrichtigungen(this.onBenachrichtigungenGeladen);
        this._markiereBenachrichtigungenAlsGelesen = new MarkiereBenachrichtigungenAlsGelesen();
        this._appNotificationContext = useAppNotificationContext();
        this._history = useHistory();

        useEffect(this.pollingIntervallChangedEffect, [this.pollingIntervall]);
        useEffect(this.refetchChangedEffect, [this._appNotificationContext.refetch]);
        useEffect(this.klingelnEffect, [this.hatUngetoasteteItems])
        useEffect(this.toasteItemsEffect, []);
    }

    private readonly pollingIntervallChangedEffect = () => {
        const interval = this.pollingIntervall;
        this._ladeBenachrichtigungen.stopPolling();

        if (this.pollingIntervall > 0) {
            this._ladeBenachrichtigungen.refetch();
            this._ladeBenachrichtigungen.startPolling(interval);
        }
    }

    private readonly refetchChangedEffect = () => {
        if (this._appNotificationContext.refetch) {
            const interval = this.pollingIntervall;
            this._ladeBenachrichtigungen.stopPolling();

            this._ladeBenachrichtigungen.refetch();
            this._appNotificationContext.benachrichtigungenGeholt();

            this._ladeBenachrichtigungen.startPolling(interval);
        }
    }

    private readonly klingelnEffect = () => {
        if (this.hatUngetoasteteItems) {
            this.setState(next => next.klingeln = true);
            const timeout = setTimeout(
                () => this.setState(next => next.klingeln = false),
                AppNotificationsModel._klingelnMillisekunden);
            return () => clearTimeout(timeout);
        }

        this.setState(next => next.klingeln = false);
        return () => {};
    }

    private readonly toasteItemsEffect = () => {
        const interval = setInterval(
            () => this.setState(next => {
                const zeit = new Date().getTime();
                next.items = next.items.map(i => {
                    if (i.toastBis.getTime() <= zeit) {
                        return {
                            ...i,
                            getoasted: true,
                        } as IAppNotificationItem;
                    }

                    return i;
                });
            }),
            AppNotificationsModel._toastIntervallMillisekunden);
        return () => clearInterval(interval);
    }


    private get pollingIntervall(): number {
        return this._appNotificationContext.pollingIntervall;
    }

    public get anzahlUngesehenerItems(): number {
        return this.state.items.filter(i => i.gesehen === false).length;
    }

    public get hatLaufendeItems(): boolean {
        return this.state.items.filter(i => i.benachrichtigung.symbol === Symbole.laufend).length > 0;
    }

    public get istDrawerOffen(): boolean {
        return this.state.istDrawerOffen;
    }

    public get klingeln(): boolean {
        return this.state.klingeln
    }

    public get hatUngetoasteteItems(): boolean {
        return this.ungetoasteteItems.length > 0;
    }

    public get ungetoasteteItems(): IAppNotificationItem[] {
        return this.state.items.filter(i => !i.getoasted);
    }

    public get ungeleseneItems(): IAppNotificationItem[] {
        return this.state.items;
    }

    public get kannAlleMarkierenAlsGelesen(): boolean {
        return this.state.items.filter(i => i.benachrichtigung.kannMarkierenAlsGelesen).length > 0;
    }

    public readonly schliesseDialog = () => this.setState(next => {
        next.itemImDialog = null;
        next.istDrawerOffen = true;
    });

    public readonly oeffneDrawer = () => {
        this.setState(next => {
            next.items = this.markiereAlleItemsAlsGesehen(next.items);
            next.istDrawerOffen = true;
        });
    };

    public readonly schliesseDrawer = () => {
        this.setState(next => next.istDrawerOffen = false);
    };

    public readonly markiereItemAlsGetoasted = (item: IAppNotificationItem) : void => {
        this.markiereItemsAlsGetoastedInternal([item]);
    }

    private readonly markiereItemsAlsGetoastedInternal = (items: IAppNotificationItem[]) : void => {
        this.setState(next => next.items = next.items.map(i => {
            const item = items.find(i2 => i2.benachrichtigung.benachrichtigungId === i.benachrichtigung.benachrichtigungId);
            if (item) {
                return {
                    ...i,
                    getoasted: true,
                } as IAppNotificationItem;
            }

            return i;
        }));
    }

    private readonly markiereAlleItemsAlsGesehen = (items: IAppNotificationItem[]) : IAppNotificationItem[] => {
        return items.map(i => {
            return {
                ...i,
                gesehen: true,
                getoasted: true,
            } as IAppNotificationItem;
        });
    }

    public readonly markiereAlleItemsAlsGelesen = (): void => {
        const items = this.state.items.filter(i => i.benachrichtigung.kannMarkierenAlsGelesen);
        if (items.length > 0 ) {
            this.markiereAlsGelesenInternal(items, true);
        }
    };

    public readonly oeffneDialogOderMarkiereItemAlsGelesen = (item: IAppNotificationItem): void => {
        if (!item.benachrichtigung.istAlleLesbar) {
            this.setState(next => next.itemImDialog = item);
        } else {
            this.markiereItemAlsGelesen(item);
        }
    }

    public readonly markiereItemAlsGelesen = (item: IAppNotificationItem): void => {
        this.markiereAlsGelesenInternal([item], false);
    };

    private readonly markiereAlsGelesenInternal = (items: IAppNotificationItem[], markiereAlle: boolean): void => {
        const input: MarkiereBenachrichtigungenAlsGelesenInput = {
            benachrichtigungen: items.map(i => {
                return {
                    benachrichtigungId: i.benachrichtigung.benachrichtigungId,
                    geaendert: i.benachrichtigung.geaendert,
                } as BenachrichtigungVersion;
            }),
            markiereAlle
        };
        this.showCoffeeMug();

        this._markiereBenachrichtigungenAlsGelesen
            .execute(input).then(() => {
                this._ladeBenachrichtigungen.refetch();
                this.hideCoffeeMug();
            });
    }

    public readonly navigiereZuReferenzFuerToast = (item: IAppNotificationItem): void => {
        if (!item.typUrl) {
            throw new Error("typUrl ungültig");
        }

        this.markiereItemAlsGetoasted(item);

        if (!(item.urlInNeuemTabOeffnen || item.urlIstDateiDownload)) {
            this._navigate(item.typUrl);
        }
    }

    public readonly navigiereZuReferenzFuerGelesen = (item: IAppNotificationItem): void => {
        if (!item.typUrl) {
            throw new Error("typUrl ungültig");
        }

        this.schliesseDrawer();
        if (!(item.urlInNeuemTabOeffnen || item.urlIstDateiDownload)) {
            this._navigate(item.typUrl);
        }
    }

    private readonly onBenachrichtigungenGeladen = (benachrichtigungen: Benachrichtigung[]): void => {
        benachrichtigungen = benachrichtigungen ?? [];
        const abgeschlosseneBenachrichtigungIds = benachrichtigungen.filter(b => b.symbol !== Symbole.laufend).map(b => b.benachrichtigungId);
        this._appNotificationContext.stoppePollingFuer(abgeschlosseneBenachrichtigungIds);

        this.setState(next => {
            const items = benachrichtigungen.map(b => this.erstelleItem(b, next.istDrawerOffen, next.items));
            next.items = items;
        });
    }

    private readonly istBenachrichtigungUnveraendert = (benachrichtigung: Benachrichtigung, vorhandeneBenachrichtigung: Benachrichtigung): boolean => {
        return (benachrichtigung.geaendert === vorhandeneBenachrichtigung.geaendert) &&
            (benachrichtigung.symbol === vorhandeneBenachrichtigung.symbol) &&
            (benachrichtigung.matchcode === vorhandeneBenachrichtigung.matchcode) &&
            (benachrichtigung.text === vorhandeneBenachrichtigung.text) &&
            (benachrichtigung.timeout === vorhandeneBenachrichtigung.timeout);
    }

    private readonly erstelleItem = (benachrichtigung: Benachrichtigung, istDrawerOffen: boolean, vorhandeneItems: IAppNotificationItem[]): IAppNotificationItem => {
        let gesehen = false;
        let getoasted = false;
        let toastBis = new Date();
        toastBis.setSeconds(toastBis.getSeconds() + AppNotificationsModel._toastSekunden);

        const vorhandenesItem = vorhandeneItems.find(i => i.benachrichtigung.benachrichtigungId === benachrichtigung.benachrichtigungId);
        if (vorhandenesItem && this.istBenachrichtigungUnveraendert(benachrichtigung, vorhandenesItem.benachrichtigung)) {
            gesehen = vorhandenesItem.gesehen;
            getoasted = vorhandenesItem.getoasted;
            toastBis = vorhandenesItem.toastBis;
        }

        const cloudTyp = cloudTypen.ermittle(benachrichtigung.referenzTyp);
        const typUrl = cloudTyp?.ermittleUrl(benachrichtigung.referenzId ?? "");

        if (istDrawerOffen) {
            gesehen = true;
            getoasted = true;
        }

        const urlInNeuemTabOeffnen = cloudTyp?.urlInNeuemTabOeffnen ?? false;
        const urlIstDateiDownload = cloudTyp?.urlIstDateiDownload ?? false;
        return { benachrichtigung, typUrl, gesehen, getoasted, toastBis, urlInNeuemTabOeffnen, urlIstDateiDownload };
    }
}
